/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useMemo } from 'react';
import { Select, Button, Typography, Tabs, Card, Row, Col } from 'antd';
import { FileExcelOutlined, DownOutlined } from '@ant-design/icons';
import { ViewByType } from '../../services/statistics.service';

interface ProfileFilterHeaderProps {
  activeMainTab?: 'profile' | 'access';
  onMainTabChange?: (val: 'profile' | 'access') => void;
  target: 'user' | 'relative' | 'all';
  onTargetChange: (val: 'user' | 'relative' | 'all') => void;
  viewBy: ViewByType;
  onViewByChange: (val: ViewByType) => void;
  onExportExcel?: () => void;
  exportLoading?: boolean;
  exportDisabled?: boolean;
}

export const ProfileFilterHeader: React.FC<ProfileFilterHeaderProps> = ({
  activeMainTab = 'profile',
  onMainTabChange,
  target,
  onTargetChange,
  viewBy,
  onViewByChange,
  onExportExcel,
  exportLoading = false,
  exportDisabled = false,
}) => {
  // Dynamically compute "Xem theo" options based on selected target profile
  const viewByOptions = useMemo(() => {
    if (target === 'user') {
      return [
        { value: 'age', label: 'Độ tuổi' },
        { value: 'gender', label: 'Giới tính' },
        { value: 'province', label: 'Tỉnh thành' },
        { value: 'health_status', label: 'Tình trạng sức khỏe' },
        { value: 'age_gender', label: 'Độ tuổi và giới tính' },
        { value: 'age_province', label: 'Độ tuổi và tỉnh thành' },
        { value: 'age_health', label: 'Độ tuổi và tình trạng sức khỏe' },
        { value: 'health_gender', label: 'Tình trạng sức khỏe và giới tính' },
        // { value: 'province_gender', label: 'Tỉnh thành và giới tính' }, // remove this option 
        { value: 'province_health', label: 'Tỉnh thành và tình trạng sức khỏe' },
        { value: 'custom', label: 'Danh sách tuỳ chỉnh' },
      ];
    } else if (target === 'relative') {
      return [
        { value: 'age', label: 'Độ tuổi' },
        { value: 'gender', label: 'Giới tính' },
        { value: 'health_status', label: 'Tình trạng sức khỏe' },
        { value: 'age_gender', label: 'Độ tuổi và giới tính' },
        { value: 'age_health', label: 'Độ tuổi và tình trạng sức khỏe' },
        { value: 'health_gender', label: 'Tình trạng sức khỏe và giới tính' },
        { value: 'custom', label: 'Danh sách tuỳ chỉnh' },
      ];
    } else {
      // target === 'all'
      return [
        { value: 'age', label: 'Độ tuổi' },
        { value: 'gender', label: 'Giới tính' },
        { value: 'health_status', label: 'Tình trạng sức khỏe' },
        { value: 'age_gender', label: 'Độ tuổi và giới tính' },
        { value: 'age_health', label: 'Độ tuổi và tình trạng sức khỏe' },
        { value: 'health_gender', label: 'Tình trạng sức khỏe và giới tính' },
        { value: 'custom', label: 'Danh sách tuỳ chỉnh' },
      ];
    }
  }, [target]);

  // When target changes, reset viewBy if selected viewBy is not in the new options list
  const handleTargetSelect = (newTarget: 'user' | 'relative' | 'all') => {
    onTargetChange(newTarget);
    if (
      newTarget !== 'user' &&
      ['province', 'province_health', 'age_province', 'province_gender'].includes(viewBy)
    ) {
      onViewByChange('age');
    }
  };

  return (
    <div css={containerStyle}>
      {/* 1. Page Title */}
      <Row css={boxStyles} justify='space-between' align='middle'>
        <Col>
          <Typography.Title css={titleStyles} level={5}>
            THỐNG KÊ
          </Typography.Title>
        </Col>
      </Row>

      {/* 2. Top Tabs: Hồ sơ | Lượt truy cập */}
      <div css={tabsRowStyle}>
        <Tabs
          activeKey={activeMainTab}
          onChange={(key) => onMainTabChange?.(key as 'profile' | 'access')}
          css={tabStyle}
        >
          <Tabs.TabPane tab="Hồ sơ" key="profile" />
          <Tabs.TabPane tab="Lượt truy cập" key="access" />
        </Tabs>
      </div>

      {/* 3. Target Profile Dropdown & Export Excel Button */}
      <Card css={cardStyle}>
        <div css={targetRowStyle}>
          <Select
            value={target}
            onChange={handleTargetSelect}
            style={{ width: 220 }}
            options={[
              { value: 'all', label: 'Tất cả' },
              { value: 'user', label: 'Hồ sơ người dùng' },
              { value: 'relative', label: 'Hồ sơ người thân' },
            ]}
          />

          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', fontWeight: 500 }}
            onClick={onExportExcel}
            loading={exportLoading}
            disabled={exportLoading || exportDisabled}
          >
            {exportLoading ? 'Đang xuất...' : 'Xuất báo cáo Excel'}
          </Button>
        </div>
      </Card>

      {/* 4. "Xem theo ▾" Dropdown Trigger Button */}
      <Card css={viewByCardStyle}>
        <div css={viewByRowStyle}>
          <Select
            value={viewBy}
            onChange={onViewByChange}
            labelRender={() => 'Xem theo'}
            style={{ minWidth: 150, maxWidth: '100%' }}
            className="xem-theo-select"
            suffixIcon={<DownOutlined style={{ color: '#fff' }} />}
            options={viewByOptions}
            dropdownMatchSelectWidth={false}
            placeholder="Xem theo"
          />
        </div>
      </Card>
    </div>
  );
};

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  margin-bottom: 1rem;
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
`;

const containerStyle = css`
  margin-bottom: 0;
`;

const tabsRowStyle = css`
  margin-bottom: 1rem;
`;

const tabStyle = css`
  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-tab {
    padding: 6px 8px;
    color: #333;
    border: 1px solid #ccc;
    background-color: #f0f0f0;
  }

  .ant-tabs-tab:not(:first-of-type) {
    margin: 0 0 0 8px !important;
  }

  .ant-tabs-tab-active {
    background-color: var(--blue-dark-color);
    border: 1px solid var(--blue-dark-color);
    .ant-tabs-tab-btn {
      color: #ffffff !important;
    }
  }

  .ant-tabs-ink-bar {
    display: none;
  }

  .ant-tabs-tab:hover {
    color: #333 !important;
  }

  .ant-tabs-tab.ant-tabs-tab-disabled {
    cursor: not-allowed;
    background-color: #f5f5f5;
    border-color: #d9d9d9;
    .ant-tabs-tab-btn {
      color: rgba(0, 0, 0, 0.25) !important;
    }
  }

  .ant-tabs-tab.ant-tabs-tab-disabled:hover .ant-tabs-tab-btn {
    color: rgba(0, 0, 0, 0.25) !important;
  }
`;

const cardStyle = css`
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const viewByCardStyle = css`
  margin-bottom: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .ant-card-body {
    padding-bottom: 0;
  }
`;

const targetRowStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const viewByRowStyle = css`
  .xem-theo-select {
    display: inline-block;
  }

  .xem-theo-select .ant-select-selector {
    background-color: #1890ff !important;
    color: #fff !important;
    border-color: #1890ff !important;
    border-radius: 4px;
    font-weight: 600;
  }

  .xem-theo-select .ant-select-selection-item {
    color: #fff !important;
    font-weight: 600;
  }

  .xem-theo-select .ant-select-arrow {
    color: #fff !important;
  }
`;
